import { splitCsvLine } from "../../shared/functions/splitCsvLine";
import type {
  SplitCostingImportError,
  SplitCostingImportWorkflowResult,
} from "../hooks/useSplitCostingImportInvocation";
import type { SplitCostingImportWarning } from "./computeSplitCostingWarnings";
import type {
  SplitCostingParseError,
  SplitCostingRow,
} from "./parseSplitCostingCsv";
import { splitCostingIsoToDisplayDate } from "./splitCostingRowValidation";

const PATH_HEADER_KEYS = [
  "division",
  "costcentre",
  "department",
  "subdepartment",
  "level6",
  "level7",
] as const;

export interface SplitCostingCsvHeaderIndices {
  columnCount: number;
  employeeCode: number;
  apportionedPercentage: number;
  path: readonly number[];
  startDate: number;
  endDate: number;
}

export interface BuildSplitCostingIssueReasonsInput {
  rows: readonly SplitCostingRow[];
  result?: SplitCostingImportWorkflowResult | null;
  warnings?: readonly SplitCostingImportWarning[];
  dismissedWarningIds?: ReadonlySet<string>;
  parseErrors?: readonly SplitCostingParseError[];
}

function normaliseHeader(value: string): string {
  return value
    .replace(/^\uFEFF/, "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");
}

/**
 * Resolves source-column positions without assuming a fixed column order.
 * Unknown columns remain part of the output and are left unchanged.
 */
export function resolveSplitCostingHeaderIndices(
  headerLine: string
): SplitCostingCsvHeaderIndices | null {
  const headers = splitCsvLine(headerLine);
  const normalised = headers.map(normaliseHeader);
  const employeeCode = normalised.indexOf("employeecode");
  if (employeeCode < 0) return null;

  return {
    columnCount: headers.length,
    employeeCode,
    apportionedPercentage: normalised.indexOf("apportionedpercentage"),
    path: PATH_HEADER_KEYS.map((key) => normalised.indexOf(key)),
    startDate: normalised.indexOf("startdate"),
    endDate: normalised.indexOf("enddate"),
  };
}

/**
 * Overlays the current in-memory row onto its original source cells. This
 * preserves unknown columns while ensuring Edit in UI values are exported.
 */
export function mergeSplitCostingRowIntoCsvCells(
  originalCells: readonly string[],
  indices: SplitCostingCsvHeaderIndices,
  row: SplitCostingRow
): string[] {
  const cells = Array.from(
    { length: indices.columnCount },
    (_, index) => originalCells[index] ?? ""
  );
  const setCell = (index: number, value: string) => {
    if (index >= 0 && index < cells.length) cells[index] = value;
  };

  setCell(indices.employeeCode, row.employeeCode);
  setCell(indices.apportionedPercentage, row.apportionedPercentage);

  const pathByLevel = new Map(
    row.path.map((part) => [part.level, part.code] as const)
  );
  indices.path.forEach((columnIndex, pathIndex) => {
    setCell(columnIndex, pathByLevel.get(pathIndex + 1) ?? "");
  });

  setCell(
    indices.startDate,
    splitCostingIsoToDisplayDate(row.startDate)
  );
  setCell(indices.endDate, splitCostingIsoToDisplayDate(row.endDate));
  return cells;
}

function appendReason(
  reasonsByFileRow: Map<number, string[]>,
  fileRow: number,
  reason: string
): void {
  if (!Number.isInteger(fileRow) || fileRow < 2 || reason === "") return;
  const reasons = reasonsByFileRow.get(fileRow);
  if (!reasons) {
    reasonsByFileRow.set(fileRow, [reason]);
  } else if (!reasons.includes(reason)) {
    reasons.push(reason);
  }
}

function appendApiError(
  reasonsByFileRow: Map<number, string[]>,
  rows: readonly SplitCostingRow[],
  error: SplitCostingImportError
): void {
  if (
    error.index === null ||
    !Number.isInteger(error.index) ||
    error.index < 0
  ) {
    return;
  }
  const sourceRow = rows[error.index];
  if (sourceRow) {
    appendReason(reasonsByFileRow, sourceRow.fileRow, error.error);
  }
}

/**
 * Combines every row-addressable issue source into one source-row map.
 * Dismissed portal warnings and batch-level prerequisite errors are excluded.
 */
export function buildSplitCostingIssueReasonsByFileRow({
  rows,
  result,
  warnings = [],
  dismissedWarningIds = new Set<string>(),
  parseErrors = [],
}: BuildSplitCostingIssueReasonsInput): Map<number, string[]> {
  const reasonsByFileRow = new Map<number, string[]>();

  for (const error of result?.errors ?? []) {
    appendApiError(reasonsByFileRow, rows, error);
  }
  for (const warning of warnings) {
    if (dismissedWarningIds.has(warning.id)) continue;
    for (const fileRow of warning.fileRows) {
      appendReason(reasonsByFileRow, fileRow, warning.message);
    }
  }
  for (const error of parseErrors) {
    if (error.fileRow !== null) {
      appendReason(reasonsByFileRow, error.fileRow, error.message);
    }
  }

  return reasonsByFileRow;
}

function neutraliseFormula(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

function escapeCsvCell(value: string): string {
  const safe = neutraliseFormula(value);
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

function findHeader(
  lines: readonly string[]
): {
  lineIndex: number;
  headerCells: string[];
  indices: SplitCostingCsvHeaderIndices;
} | null {
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const indices = resolveSplitCostingHeaderIndices(lines[lineIndex]);
    if (indices) {
      const headerCells = splitCsvLine(lines[lineIndex]);
      if (headerCells.length > 0) {
        headerCells[0] = headerCells[0].replace(/^\uFEFF/, "");
      }
      return { lineIndex, headerCells, indices };
    }
  }
  return null;
}

/**
 * Builds a safe, re-uploadable issues CSV using the original columns plus a
 * trailing Reason column. Parsed rows are rebuilt from current edited values;
 * parse-failed rows retain their original source values.
 */
export function buildSplitCostingImportIssuesCsv(
  rawCsvText: string,
  reasonsByFileRow: ReadonlyMap<number, readonly string[]>,
  rows: readonly SplitCostingRow[] = []
): string {
  if (rawCsvText === "" || reasonsByFileRow.size === 0) return "";

  const lines = rawCsvText
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");
  const header = findHeader(lines);
  if (!header) return "";

  const rowsByFileRow = new Map(rows.map((row) => [row.fileRow, row]));
  const outputRows: string[] = [
    [...header.headerCells, "Reason"].map(escapeCsvCell).join(","),
  ];

  for (
    let lineIndex = header.lineIndex + 1;
    lineIndex < lines.length;
    lineIndex += 1
  ) {
    const fileRow = lineIndex + 1;
    const reasons = reasonsByFileRow.get(fileRow);
    if (!reasons || reasons.length === 0) continue;

    const originalCells = splitCsvLine(lines[lineIndex]);
    const currentRow = rowsByFileRow.get(fileRow);
    const cells = currentRow
      ? mergeSplitCostingRowIntoCsvCells(
          originalCells,
          header.indices,
          currentRow
        )
      : Array.from(
          { length: header.indices.columnCount },
          (_, index) => originalCells[index] ?? ""
        );
    outputRows.push(
      [...cells, reasons.join("; ")].map(escapeCsvCell).join(",")
    );
  }

  return outputRows.length > 1 ? outputRows.join("\n") : "";
}

export function splitCostingIssuesFilename(
  originalFileName: string | null
): string {
  const baseName = originalFileName?.replace(/\.csv$/i, "");
  return `${baseName || "split-costing-import"}-issues.csv`;
}

/** Starts a browser download without a server round-trip. */
export function downloadSplitCostingImportIssuesCsv(
  rawCsvText: string,
  reasonsByFileRow: ReadonlyMap<number, readonly string[]>,
  rows: readonly SplitCostingRow[],
  filename: string
): void {
  const csv = buildSplitCostingImportIssuesCsv(
    rawCsvText,
    reasonsByFileRow,
    rows
  );
  if (csv === "") return;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
